import type { ApiMe } from 'src/types/wallet';

import { useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';

import axios from 'src/utils/axios';
import { sessionEpoch, subscribeUserSession } from 'src/utils/userSession';

import { QueryKeys } from './QueryKeys';

/**
 * The query behind `useAccountWallet`, for one epoch of the tab. Apart from the hook so a test can
 * fill the cache exactly as the hook would.
 *
 * The answer is the session's that the browser's cookie names at the moment the request leaves
 * (api#454); the key is the epoch that moment belongs to, so an answer never lands under the key of
 * a session that began after it was asked.
 */
export const accountWalletQuery = (epoch: number | null, enabled = true) => ({
  enabled: enabled && epoch !== null,
  queryKey: QueryKeys.accountWalletFor(epoch ?? 0),
  queryFn: async (): Promise<ApiMe> => {
    const response = await axios.get<ApiMe>(`/wallet/auth/me`);
    return response.data;
  },
});

/**
 * The wallet linked to the signed-in account — its primary one, read under the account session:
 * `/wallet/auth/me` is wallet-scoped and the guard resolves the session to that wallet. It is the
 * wallet api accepts as the second factor and the one the balances on Profile belong to, and it
 * may well differ from whatever wallet is connected in this browser right now. (Until step 3 of
 * api#402 retires the route, this is where the storefront reads the wallet's family and balance;
 * the address and family are on `/user/auth/me` as well.)
 *
 * Only enable it for an account that has a linked wallet: without one the endpoint answers
 * 403 WALLET_NOT_LINKED.
 *
 * Keyed by the epoch of the tab, the way `/user/auth/me` is (app#217): a new sign-in is a new key
 * and a fresh answer. The key used to be one for everybody, and answers here never go stale — so on
 * a shared device the next account to sign in in the same tab, after the previous one's session had
 * expired, was handed the previous account's primary wallet, and with it the wallet family its
 * email change is signed with (app#247 review). The epoch comes from the store `useUserSession`
 * reads; its server snapshot is the same read, since the storefront only ever renders statically
 * in tests.
 */
export function useAccountWallet(enabled: boolean) {
  const epoch = useSyncExternalStore(subscribeUserSession, sessionEpoch, sessionEpoch);

  return useQuery(accountWalletQuery(epoch, enabled));
}
